'use client'
import React, { useState, useRef, useEffect, forwardRef } from 'react'
import { debounce } from 'es-toolkit'
import { useInView } from 'react-intersection-observer'
import { cn } from '../../helpers/utils.js'
import { withLayout } from '../../shared/Styles.js'
import Slogan from '../Slogan/index.js'
import { YouTubePlayer } from '../VideoModal/YouTubePlayer.js'
import { Grid, GridItem } from '../../components/grid.js'
import { Container } from '../../components/container.js'
import BrandEquity from '../BrandEquity/index.js'
import MemberEquity from '../MemberEquity/index.js'
import Spacer from '../Spacer/index.js'
import gsap from 'gsap'
import { convertLexicalToHTML } from '@payloadcms/richtext-lexical/html'

import type { HTMLConvertersFunction } from '@payloadcms/richtext-lexical/html'

import { useMediaQuery } from 'react-responsive'
import type { MediaPlayerBaseProps, MediaPlayerComponent } from './types.js'
import ScrollLoadVideo from '../../helpers/ScrollLoadVideo.js'

const htmlConverters: HTMLConvertersFunction = ({ defaultConverters }) =>
  ({
    ...defaultConverters,
    text: (args: any) => {
      const { node } = args

      // 检查是否有自定义 color
      if (node.$ && node.$.color) {
        return `<span class="lexical-${node.$.color}">${node.text}</span>`
      }
      return node.text
    },
  }) as any

const SubContainer = ({
  children,
  spaceY,
  className,
}: {
  children: React.ReactNode
  spaceY?: string
  className?: string
}) => {
  const isMobile = useMediaQuery({ query: '(max-width: 768px)' })
  return (
    <Container spaceY={spaceY} className={cn('!bg-transparent', className)}>
      <Grid>
        {!isMobile && <GridItem span={1} />}
        <GridItem span={isMobile ? 12 : 10}>{children}</GridItem>
        {!isMobile && <GridItem span={1} />}
      </Grid>
    </Container>
  )
}

const SubBox = React.forwardRef<
  {
    mediaPlayerStickyRef: HTMLDivElement
  },
  {
    children?: React.ReactNode
    id?: string
    components?: MediaPlayerComponent[]
  }
>(({ children, id, components }, ref) => {
  const mediaPlayerStickyRef = useRef<HTMLDivElement>(null)

  return (
    <div ref={mediaPlayerStickyRef} className="relative z-10">
      <>{children}</>
      <>
        {components?.map((componentData: any, index: number) => {
          switch (componentData.blockType) {
            case 'ipc-brand-equity': {
              return (
                <div key={`${componentData.blockType}-${index}`} className="relative z-20 bg-transparent">
                  <SubContainer spaceY="none">
                    <BrandEquity data={componentData} style={componentData?.style} />
                  </SubContainer>
                </div>
              )
            }
            case 'ipc-member-equity': {
              return (
                <div key={`${componentData.blockType}-${index}`} className="relative z-20 w-full bg-transparent">
                  <SubContainer spaceY="none">
                    <MemberEquity data={componentData} className="w-full" style={componentData?.style} />
                  </SubContainer>
                </div>
              )
            }
            case 'ipc-spacer': {
              return (
                <Spacer
                  key={`${componentData.blockType}-${index}`}
                  data={componentData}
                  style={componentData?.style}
                  className="!bg-transparent"
                />
              )
            }
            default:
              return null
          }
        })}
      </>
    </div>
  )
})

SubBox.displayName = 'SubBox'

const MediaPlayerSticky = forwardRef<
  {
    mediaPlayerStickyRef: HTMLDivElement
  },
  MediaPlayerBaseProps
>(
  (
    {
      className = '',
      wrapperClassName = '',
      id,
      data: {
        title,
        videoTitle,
        mobVideo,
        mobImg,
        img,
        isYouTube,
        youtubePcId,
        youtubeMobileId,
        video,
        theme,
        shape,
        components,
      },
      ...rest
    },
    ref
  ) => {
    const videoRef = useRef<HTMLVideoElement>(null)
    const boxRef = useRef<HTMLDivElement>(null)
    const { ref: inViewRef, inView } = useInView()
    const [screenHeight, setScreenHeight] = useState(0)
    const [isPlaying, setIsPlaying] = useState(false)
    const [isEntered, setIsEntered] = useState(false)
    const wrapperRef = useRef<HTMLDivElement>(null)

    const title_html =
      typeof title === 'string' ? title : title && convertLexicalToHTML({ data: title, converters: htmlConverters })
    typeof videoTitle === 'string'
      ? videoTitle
      : videoTitle && convertLexicalToHTML({ data: videoTitle, converters: htmlConverters })

    const [btb, setbtb] = useState(0)
    const [isMobile, setIsMobile] = useState(false)
    const mediaQuery = useMediaQuery({ query: '(max-width: 768px)' })

    useEffect(() => {
      setIsMobile(mediaQuery)
    }, [mediaQuery])

    useEffect(() => {
      if (inView && !isPlaying) {
        videoRef.current?.play()
        setIsPlaying(true)
      }
    }, [inView, isPlaying])

    useEffect(() => {
      const debouncedHandleResize = debounce(() => {
        if (boxRef.current) {
          const rect = boxRef.current.getBoundingClientRect()
          const screenHeight = isMobile ? window.screen.height : window.innerHeight
          const scrollTop = window.scrollY || window.pageYOffset
          const elementBottomToPageTop = rect.bottom + scrollTop
          const pageHeight = document.documentElement.scrollHeight
          const distanceToPageBottom = pageHeight - elementBottomToPageTop
          setbtb(distanceToPageBottom > screenHeight ? screenHeight : distanceToPageBottom)
          setScreenHeight(screenHeight)
        }
      }, 2000)
      debouncedHandleResize()
      window.addEventListener('resize', debouncedHandleResize)
      return () => {
        window.removeEventListener('resize', debouncedHandleResize)
      }
    }, [])

    useEffect(() => {
      function gsapResize() {
        const height = isMobile ? window.screen.height : window.innerHeight
        gsap
          .timeline({
            scrollTrigger: {
              trigger: `#${id}`,
              start: 'top top',
              end: `top+=${height * 1.8}px bottom`,
              scrub: 0.5,
            },
          })
          .from(`#${id} .media-content`, { top: '50%', transform: 'translate(-50%, -50%)' })
          .to(`.${id} .sticky-cover`, {
            backdropFilter: 'blur(8px)',
            duration: 0.4,
          })
          .to(
            `#${id} .media-content`,
            {
              top: '100%',
              transform: 'translate(-50%, -100%)',
              duration: 1,
            },
            '<'
          )
      }
      if (inView) {
        gsapResize()
      }
    }, [inView])

    return (
      <>
        <div
          id={id}
          {...rest}
          className={cn('relative z-20 h-screen w-full', className, {
            'aiui-dark': theme === 'dark',
            'rounded-box': shape === 'rounded',
          })}
          ref={wrapperRef}
        >
          <div
            ref={inViewRef}
            className="media-content translate-z-0 absolute left-1/2 top-1/2 z-10 w-full -translate-x-1/2 -translate-y-1/2 antialiased"
            style={{ willChange: 'transform', contain: 'paint' }}
          >
            <SubContainer spaceY="tablet:my-[32px] my-[24px] space-y-4">
              <Slogan className="sticky-title antialiased" data={{ title: title_html || '', theme, tag: 'h2' }} />
            </SubContainer>
          </div>
        </div>

        <div
          ref={boxRef}
          style={{ marginBottom: `-${btb}px`, zIndex: inView ? 2 : 1 }}
          className={cn(id, 'relative mt-[-200vh]', wrapperClassName)}
        >
          <div className="sticky top-0 ">
            <div className={cn('media-cover', 'relative h-screen w-full')}>
              {isYouTube ? (
                <YouTubePlayer youTubeId={isMobile ? youtubeMobileId || youtubePcId : youtubePcId} />
              ) : (
                video?.url && (
                  <ScrollLoadVideo
                    videoRef={videoRef}
                    poster={isMobile && mobImg?.url ? mobImg?.url : img?.url}
                    src={isMobile && mobVideo?.url ? mobVideo?.url : video?.url}
                    className="lg-desktop:aspect-w-[1920] lg-desktop:aspect-h-[930] lg:aspect-w-[1440] lg:aspect-h-[700] desktop:aspect-w-[1024] desktop:aspect-h-[520] tablet:aspect-w-[768] tablet:aspect-h-[660] aspect-w-[390] aspect-h-[660]"
                    videoClassName=" object-cover object-[82%]"
                    muted
                    loop={false}
                    playsInline
                    autoplay={false}
                    onEnded={() => {
                      setIsEntered(true)
                    }}
                  />
                )
              )}
              {img?.url && (!video?.url || isEntered) && (
                <img
                  src={isMobile && mobImg?.url ? mobImg?.url : img?.url}
                  alt={img?.alt}
                  className="z-1 absolute left-0 top-0 size-full object-cover  object-[82%]"
                  loading="lazy"
                />
              )}
              <div
                className="sticky-cover absolute left-0 top-0 z-10 size-full"
                style={{
                  backdropFilter: 'blur(0px)',
                  WebkitBackdropFilter: 'blur(0px)',
                  willChange: 'backdrop-filter',
                }}
              />
            </div>
          </div>

          <div className="relative h-screen w-full" />
          <SubBox components={components}></SubBox>
          <div className="relative h-screen w-full" />
        </div>
      </>
    )
  }
)
MediaPlayerSticky.displayName = 'MediaPlayerSticky'

export default withLayout(MediaPlayerSticky)
